import { useMemo } from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';

import { BLOCK_SERVICE_API_URL } from './services/snippetMediaClient';
import { SnippetBodyFormat, type SnippetProduct, SnippetScope, type SnippetData, type SnippetsProvider, type Snippet } from './types';

const SNIPPETS_PATH = '/snippets';

export interface FetchSnippetDataParams {
	format: SnippetBodyFormat;
	product?: SnippetProduct;
	scope: SnippetScope;
}

export type FetchSnippetsParams = Omit<FetchSnippetDataParams, 'scope'>;

export type SnippetProviderParams = FetchSnippetsParams;

type SnippetSettingsResponse = {
	sharingEnabled: boolean;
};

export interface FetchSnippetsResponse {
	error: Error[];
	success: SnippetData[];
}

export const getIsSnippetSharingForSiteEnabled = async (): Promise<boolean> => {
	try {
		const response = await fetch(`${BLOCK_SERVICE_API_URL}${SNIPPETS_PATH}/settings`, {
			method: 'GET',
		});
		const data: SnippetSettingsResponse = await response.json();
		return data.sharingEnabled;
	} catch (error) {
		return false;
	}
};

export const fetchSnippets = async (params: FetchSnippetsParams): Promise<SnippetData[]> => {
	const isSnippetSharingForSiteEnabled = await getIsSnippetSharingForSiteEnabled();
	const [globalSnippetsResponse, siteSnippetsResponse, userSnippetsResponse] = await Promise.allSettled([
		fetchSnippetsData({ ...params, scope: SnippetScope.GLOBAL }),
		isSnippetSharingForSiteEnabled ? fetchSnippetsData({ ...params, scope: SnippetScope.SITE }) : Promise.resolve({ success: [] }),
		fetchSnippetsData({ format: params.format, scope: SnippetScope.USER }), // exclude product for user snippets
	]);

	const globalSnippets = globalSnippetsResponse.status === 'fulfilled' ? globalSnippetsResponse.value : { success: [] };
	const siteSnippets = siteSnippetsResponse.status === 'fulfilled' ? siteSnippetsResponse.value :  { success: [] };
	const userSnippets = userSnippetsResponse.status === 'fulfilled' ? userSnippetsResponse.value : { success: [] };

	return [...globalSnippets.success, ...siteSnippets.success, ...userSnippets.success];
}

export const fetchSnippetsData = async (params: FetchSnippetDataParams): Promise<FetchSnippetsResponse> => {
	const queryParams = params.product ? new URLSearchParams({
		format: params.format,
		product: params.product,
		scope: params.scope,
	}) : new URLSearchParams({
		format: params.format,
		scope: params.scope,
	});

	const response = await fetch(
		`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}?${queryParams.toString()}`,
		{
			method: 'GET',
		},
	);

	if (!response.ok) {
		throw new Error(`Failed to fetch snippets for scope: ${params.scope}`);
	}

	return await response.json();
};

export const createSnippetsProvider = (params: SnippetProviderParams): SnippetsProvider => ({
	getSnippets: async (): Promise<Snippet[]> => {
		let snippets = await fetchSnippets(params);

		if (!snippets) {
			return [];
		}

		if (!Array.isArray(snippets)) {
			snippets = [snippets];
		}

		return snippets
			.map((snippet) => {
				const snippetBody = parseSnippetContent(snippet.body);
				if (snippetBody === undefined) {
					return undefined;
				}

				return { ...snippet, body: snippetBody } as Snippet;
			})
			.filter((snippet: Snippet | undefined): snippet is Snippet => snippet !== undefined);
	},
});

function filterValidAdfNodes(nodes: (ADFEntity | null | undefined)[]): ADFEntity[] {
	return nodes.filter((node): node is ADFEntity => node !== undefined && node !== null);
}

const parseSnippetContent = (content: string): ADFEntity | undefined => {
	try {
		const parsedContent = JSON.parse(content);

		// Handle fragment (array of nodes without doc wrapper)
		if (Array.isArray(parsedContent)) {
			return wrapAdfWithDocNode(filterValidAdfNodes(parsedContent));
		}

		const adfContent = parsedContent as ADFEntity;

		if (adfContent.type === 'doc' && Array.isArray(adfContent.content)) {
			return { ...adfContent, content: filterValidAdfNodes(adfContent.content) };
		}

		return wrapAdfWithDocNode(adfContent);
	} catch (error) {
		return undefined;
	}
};

const wrapAdfWithDocNode = (adf: ADFEntity | ADFEntity[]): ADFEntity => {
	const docType = 'doc';
	const isArray = Array.isArray(adf);
	if (!isArray && adf.type === docType) {
		return adf;
	}

	const content: ADFEntity[] = isArray ? adf : [adf];
	return {
		version: 1,
		type: docType,
		content,
	};
};

export interface SnippetsPluginOptionsParams {
	product: SnippetProduct;
}

export const useSnippetsPluginOptions = ({
	product,
}: SnippetsPluginOptionsParams): { snippetsProvider: SnippetsProvider } =>
	useMemo(
		() => ({
			snippetsProvider: createSnippetsProvider({
				format: SnippetBodyFormat.ADF,
				product,
			}),
		}),
		[product],
	);
